// src/components/System/SmsBalance.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import {
  getBalances,
  getBalance,
  addBalance,
  deductBalance,
  setBalance,
  getTransactions,
  clearApiMessage,
  clearError,
  clearSmsBalanceState,
  clearCurrentBalance,
  selectBalances,
  selectCurrentBalance,
  selectTransactions,
  selectTransactionPagination,
  selectSmsBalanceLoading,
  selectSmsBalanceSubmitting,
  selectSmsBalanceApiMessage,
  selectSmsBalanceError,
  selectSmsBalanceSuccess,
} from '../../store/slices/system/smsBalanceSlice';
import {
  Coins,
  Plus,
  Minus,
  Edit,
  Search,
  Loader2,
  Eye,
  RefreshCw,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  LayoutGrid,
  List,
  ClipboardList,
  AlertTriangle,
  Building2,
} from 'lucide-react';

// =============== INNER COMPONENT FOR TRANSACTION MODAL ===============
const TransactionModalContent = ({ orgId, orgName, closeModal }) => {
  const dispatch = useDispatch();
  const transactions = useSelector(selectTransactions) || [];
  const pagination = useSelector(selectTransactionPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  };
  const loading = useSelector(selectSmsBalanceLoading);

  // Fetch transactions when modal opens or orgId changes
  useEffect(() => {
    if (orgId) {
      dispatch(getTransactions({ id: orgId, page: 1, perPage: 20 }));
    }
  }, [dispatch, orgId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status) => {
    const labels = { add: 'Added', deduct: 'Deducted', set: 'Set' };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = { add: 'var(--green)', deduct: 'var(--red)', set: 'var(--blue)' };
    return colors[status] || 'var(--text-mid)';
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      dispatch(getTransactions({ id: orgId, page, perPage: pagination.per_page }));
    }
  };

  return (
    <div>
      <div className="modal-header">
        <div className="modal-title">
          <ClipboardList size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />
          Transaction History – {orgName}
        </div>
        <button className="modal-close" onClick={closeModal}>×</button>
      </div>
      <div className="modal-body">
        {loading && transactions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Loader2 size={24} className="animate-spin" />
          </div>
        ) : transactions.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table className="table-wrap">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Balance After</th>
                  <th>Reason</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(tx => (
                  <tr key={tx.id}>
                    <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(tx.created_at)}</td>
                    <td>
                      <span className="badge" style={{ background: `${getStatusColor(tx.type)}18`, color: getStatusColor(tx.type) }}>
                        {getStatusLabel(tx.type)}
                      </span>
                    </td>
                    <td style={{ fontWeight: '600', color: tx.amount > 0 ? 'var(--green)' : 'var(--red)' }}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}
                    </td>
                    <td>{tx.balance_after}</td>
                    <td style={{ fontSize: '12px' }}>{tx.reason || '—'}</td>
                    <td style={{ fontSize: '12px' }}>
                      {tx.creator?.first_name ? `${tx.creator.first_name} ${tx.creator.last_name}` : 'System'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pagination.last_page > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px', gap: '4px' }}>
                {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`btn btn-sm ${page === pagination.current_page ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => handlePageChange(page)}
                    disabled={loading}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-light)' }}>
            No transactions found
          </div>
        )}
      </div>
      <div className="modal-footer">
        <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
      </div>
    </div>
  );
};

// =============== MAIN COMPONENT ===============
const SmsBalance = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();

  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [viewMode, setViewMode] = useState('grid');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionType, setActionType] = useState('add'); // 'add', 'deduct', 'set'

  // ==================== REFS ====================
  const amountRef = useRef(null);
  const reasonRef = useRef(null);

  // Track last toast message
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const balances = useSelector(selectBalances) || [];
  const currentBalance = useSelector(selectCurrentBalance);
  const transactions = useSelector(selectTransactions) || [];
  const transactionPagination = useSelector(selectTransactionPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  };
  const loading = useSelector(selectSmsBalanceLoading);
  const isSubmittingRedux = useSelector(selectSmsBalanceSubmitting);
  const apiMessage = useSelector(selectSmsBalanceApiMessage);
  const error = useSelector(selectSmsBalanceError);
  const success = useSelector(selectSmsBalanceSuccess);

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(getBalances());
      } catch (err) {
        console.error('Error loading balances:', err);
      }
    };
    loadData();

    return () => {
      dispatch(clearSmsBalanceState());
    };
  }, [dispatch]);

  // Display API messages
  useEffect(() => {
    const msg = apiMessage || (error ? error : null);
    if (!msg || !showToast || !shouldShowApiToast(msg, !!error)) return;

    const messageKey = `${msg}_${error ? 'error' : 'success'}`;
    const now = Date.now();

    if (lastToastRef.current.message === messageKey &&
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }

    lastToastRef.current = { message: messageKey, timestamp: now };

    const toastType = error ? 'error' : 'success';
    showToast(msg, toastType);

    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((msg, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(msg, type);
    }
  }, [showToast]);

  // ==================== PAGINATION ====================
  const totalItems = balances.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentPageNum = currentPage;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) {
      setCurrentPage(currentPageNum - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) {
      setCurrentPage(currentPageNum + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== HELPERS ====================

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      add: 'Added',
      deduct: 'Deducted',
      set: 'Set',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      add: 'var(--green)',
      deduct: 'var(--red)',
      set: 'var(--blue)',
    };
    return colors[status] || 'var(--text-mid)';
  };

  // ==================== FILTER BALANCES ====================
  const filteredBalances = balances
    .filter(org => org.id)
    .filter(org =>
      org.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  // ==================== MODAL HANDLERS ====================

  const openActionModal = (orgId, orgName, type) => {
    setActionType(type);
    setIsSubmitting(false);

    setTimeout(() => {
      if (amountRef.current) amountRef.current.value = '';
      if (reasonRef.current) reasonRef.current.value = '';
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title">
            {type === 'add' && <Plus size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />}
            {type === 'deduct' && <Minus size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />}
            {type === 'set' && <Edit size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />}
            {type.charAt(0).toUpperCase() + type.slice(1)} Credits – {orgName}
          </div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">
              {type === 'add' ? 'Amount to Add' :
               type === 'deduct' ? 'Amount to Deduct' :
               'New Balance'}
            </label>
            <input
              className="form-input"
              type="number"
              min={type === 'set' ? 0 : 1}
              placeholder={type === 'set' ? 'e.g. 200' : 'e.g. 50'}
              ref={amountRef}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Reason (optional)</label>
            <input
              className="form-input"
              placeholder="e.g. Monthly topup"
              ref={reasonRef}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            disabled={isSubmitting || isSubmittingRedux}
            onClick={async () => {
              if (!orgId) {
                showToastMessage('Organization not found. Please refresh the list.', 'error');
                return;
              }

              const amountVal = parseInt(amountRef.current?.value);
              if (!amountVal || amountVal <= 0 || (type === 'set' && amountVal < 0)) {
                showToastMessage('Please enter a valid amount', 'error');
                return;
              }
              const reasonVal = reasonRef.current?.value?.trim() || '';

              setIsSubmitting(true);
              try {
                let result;
                const data = {
                  id: orgId,
                  amount: amountVal,
                  reason: reasonVal || undefined,
                };
                if (type === 'add') {
                  result = await dispatch(addBalance(data)).unwrap();
                } else if (type === 'deduct') {
                  result = await dispatch(deductBalance(data)).unwrap();
                } else if (type === 'set') {
                  result = await dispatch(setBalance({
                    id: orgId,
                    balance: amountVal,
                    reason: reasonVal || undefined,
                  })).unwrap();
                }
                if (result.success) {
                  closeModal();
                  await dispatch(getBalances());
                  showToastMessage(`✅ ${type.charAt(0).toUpperCase() + type.slice(1)} successful!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Action failed', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting || isSubmittingRedux ? 'Processing...' : 'Confirm'}
          </button>
        </div>
      </div>
    );
  };

  const openTransactionsModal = (orgId, orgName) => {
    // Open modal with the dedicated inner component that re-renders
    openModal(
      <TransactionModalContent
        orgId={orgId}
        orgName={orgName}
        closeModal={closeModal}
      />
    );
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderGridView = () => {
    const currentItems = filteredBalances.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );

    return (
      <div className="sender-ids-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
        {currentItems.map(org => {
          const color = org.sms_balance > 50 ? '#22c55e' :
                        org.sms_balance > 10 ? '#f59e0b' : '#ef4444';
          return (
            <div key={org.id} className="card" style={{ borderTop: `3px solid ${color}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                <div style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '12px',
                  background: `${color}18`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '22px',
                  flexShrink: 0,
                }}>
                  <Building2 size={22} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--navy)' }}>{org.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                    Balance: <strong>{org.sms_balance}</strong> credits
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => openTransactionsModal(org.id, org.name)}
                    title="View transactions"
                    disabled={loading}
                  >
                    <Eye size={14} />
                  </button>
                </div>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 10px',
                background: 'var(--cream)',
                borderRadius: '6px',
                marginBottom: '10px',
              }}>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                  Last updated: {org.last_balance_update ? formatDate(org.last_balance_update) : 'Never'}
                </div>
                {org.sms_balance < 10 && (
                  <span className="badge badge-red" style={{ fontSize: '10px' }}>Low Balance</span>
                )}
              </div>

              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-sm btn-icon btn-add"
                  onClick={() => openActionModal(org.id, org.name, 'add')}
                  disabled={isSubmitting || isSubmittingRedux}
                  title="Add credits"
                  style={{
                    background: 'var(--green-bg)',
                    color: 'var(--green)',
                    border: '1px solid #a7d7be',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '12px',
                  }}
                >
                  <Plus size={14} /> 
                </button>
                <button
                  className="btn btn-sm btn-icon btn-deduct"
                  onClick={() => openActionModal(org.id, org.name, 'deduct')}
                  disabled={isSubmitting || isSubmittingRedux || org.sms_balance === 0}
                  title="Deduct credits"
                  style={{
                    background: 'var(--red-bg)',
                    color: 'var(--red)',
                    border: '1px solid #fca5a5',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '12px',
                  }}
                >
                  <Minus size={14} /> 
                </button>
                <button
                  className="btn btn-sm btn-icon btn-set"
                  onClick={() => openActionModal(org.id, org.name, 'set')}
                  disabled={isSubmitting || isSubmittingRedux}
                  title="Set balance"
                  style={{
                    background: 'var(--blue-bg)',
                    color: 'var(--blue)',
                    border: '1px solid #93c5fd',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '12px',
                  }}
                >
                  <Edit size={14} /> 
                </button>
                <button
                  className="btn btn-ghost btn-sm btn-icon"
                  onClick={() => openTransactionsModal(org.id, org.name)}
                  title="View transactions"
                  disabled={loading}
                >
                  <Eye size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderListView = () => {
    const currentItems = filteredBalances.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );

    return (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Organization</th>
                <th>Balance</th>
                <th>Last Updated</th>
                <th style={{ minWidth: '200px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length > 0 ? (
                currentItems.map(org => (
                  <tr key={org.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Building2 size={16} color="var(--navy)" />
                        <span style={{ fontWeight: '600', color: 'var(--navy)' }}>{org.name}</span>
                      </div>
                    </td>
                    <td>
                      <span style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: org.sms_balance > 50 ? 'var(--green)' :
                               org.sms_balance > 10 ? 'var(--amber)' : 'var(--red)',
                      }}>
                        {org.sms_balance}
                      </span>
                    </td>
                    <td style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                      {org.last_balance_update ? formatDate(org.last_balance_update) : 'Never'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-sm btn-icon btn-add"
                          onClick={() => openActionModal(org.id, org.name, 'add')}
                          disabled={isSubmitting || isSubmittingRedux}
                          style={{
                            background: 'var(--green-bg)',
                            color: 'var(--green)',
                            border: '1px solid #a7d7be',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '12px',
                          }}
                        >
                          <Plus size={14} /> 
                        </button>
                        <button
                          className="btn btn-sm btn-icon btn-deduct"
                          onClick={() => openActionModal(org.id, org.name, 'deduct')}
                          disabled={isSubmitting || isSubmittingRedux || org.sms_balance === 0}
                          style={{
                            background: 'var(--red-bg)',
                            color: 'var(--red)',
                            border: '1px solid #fca5a5',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '12px',
                          }}
                        >
                          <Minus size={14} /> 
                        </button>
                        <button
                          className="btn btn-sm btn-icon btn-set"
                          onClick={() => openActionModal(org.id, org.name, 'set')}
                          disabled={isSubmitting || isSubmittingRedux}
                          style={{
                            background: 'var(--blue-bg)',
                            color: 'var(--blue)',
                            border: '1px solid #93c5fd',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '12px',
                          }}
                        >
                          <Edit size={14} /> 
                        </button>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => openTransactionsModal(org.id, org.name)}
                          title="View transactions"
                          disabled={loading}
                        >
                          <Eye size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', padding: '40px 0' }}>
                    <div className="empty-state">
                      <div className="empty-icon"><Coins size={40} strokeWidth={1.5} /></div>
                      <div className="empty-title">No organizations found</div>
                      <div className="empty-text">Try adjusting your search</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ==================== MAIN RENDER ====================

  const totalBalance = balances.reduce((sum, org) => sum + (org.sms_balance || 0), 0);
  const totalOrgs = balances.length;
  const lowBalanceOrgs = balances.filter(org => org.sms_balance < 10).length;
  const healthyOrgs = balances.filter(org => org.sms_balance >= 50).length;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: 'var(--navy)' }}>
            <Coins size={20} style={{ verticalAlign: '-4px' }} /> SMS Balance Management
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
            Manage SMS credits for all organizations.
          </p>
        </div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => dispatch(getBalances())}
          disabled={loading}
          title="Refresh balances"
        >
          <RefreshCw size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />
          Refresh
        </button>
      </div>

      {/* ===== STATS GRID ===== */}
      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold">
          <div className="stat-label">Total Organizations</div>
          <div className="stat-value">{totalOrgs}</div>
          <div className="stat-change up">↑ all</div>
          <div className="stat-icon"><Building2 size={20} /></div>
        </div>

        <div className="stat-card green">
          <div className="stat-label">Total Credits</div>
          <div className="stat-value">{totalBalance.toLocaleString()}</div>
          <div className="stat-change up">↑ available</div>
          <div className="stat-icon"><Coins size={20} /></div>
        </div>

        <div className="stat-card amber">
          <div className="stat-label">Low Balance (&lt;10)</div>
          <div className="stat-value">{lowBalanceOrgs}</div>
          <div className="stat-change down">needs topup</div>
          <div className="stat-icon"><AlertTriangle size={20} /></div>
        </div>

        <div className="stat-card blue">
          <div className="stat-label">Healthy (≥50)</div>
          <div className="stat-value">{healthyOrgs}</div>
          <div className="stat-change up">↑ good</div>
          <div className="stat-icon"><CheckCircle2 size={20} /></div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
            <span className="search-icon"><Search size={14} /></span>
            <input
              type="text"
              placeholder="Search organizations..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              disabled={loading}
            />
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            {filteredBalances.length} organizations
          </div>
          <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
            <button
              className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid size={14} style={{ verticalAlign: '-2px' }} /> Grid
            </button>
            <button
              className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
              onClick={() => setViewMode('list')}
            >
              <List size={14} style={{ verticalAlign: '-2px' }} /> List
            </button>
          </div>
        </div>
      </div>

      {/* List / Grid */}
      {loading && filteredBalances.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
          <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading balances...</div>
        </div>
      ) : (
        <>
          {filteredBalances.length > 0 ? (
            viewMode === 'grid' ? renderGridView() : renderListView()
          ) : (
            <div className="empty-state">
              <div className="empty-icon"><Coins size={40} strokeWidth={1.5} /></div>
              <div className="empty-title">No organizations found</div>
              <div className="empty-text">Try adjusting your search</div>
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          marginTop: '20px',
          padding: '16px',
          background: 'var(--white)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow)',
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            Showing <strong>{((currentPage - 1) * itemsPerPage) + 1}</strong> to{' '}
            <strong>{Math.min(currentPage * itemsPerPage, filteredBalances.length)}</strong> of{' '}
            <strong>{filteredBalances.length}</strong> organizations
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
              <select
                className="form-select"
                style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                disabled={loading}
              >
                <option value={6}>6</option>
                <option value={12}>12</option>
                <option value={18}>18</option>
                <option value={24}>24</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={goToPreviousPage}
                disabled={currentPage === 1 || loading}
                style={{
                  opacity: currentPage === 1 ? 0.5 : 1,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                }}
              >
                ← Prev
              </button>

              {getPageNumbers().map(page => (
                <button
                  key={page}
                  className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => goToPage(page)}
                  disabled={loading}
                >
                  {page}
                </button>
              ))}

              <button
                className="btn btn-ghost btn-sm"
                onClick={goToNextPage}
                disabled={currentPage === totalPages || loading}
                style={{
                  opacity: currentPage === totalPages ? 0.5 : 1,
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                }}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }
        .stat-card {
          position: relative;
          padding: 16px 20px;
          background: var(--white);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          box-shadow: var(--shadow);
          transition: transform 0.2s, box-shadow 0.2s;
          cursor: default;
        }
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }
        .stat-card .stat-label {
          font-size: 12px;
          color: var(--text-light);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 600;
        }
        .stat-card .stat-value {
          font-size: 28px;
          font-weight: 700;
          font-family: 'Playfair Display', serif;
          color: var(--navy);
          margin: 4px 0 2px;
        }
        .stat-card .stat-change {
          font-size: 12px;
          font-weight: 600;
        }
        .stat-card .stat-change.up {
          color: var(--green);
        }
        .stat-card .stat-change.down {
          color: var(--red);
        }
        .stat-card .stat-icon {
          position: absolute;
          top: 16px;
          right: 16px;
          opacity: 0.2;
          color: var(--navy);
        }
        .stat-card.gold .stat-icon { color: var(--gold); }
        .stat-card.green .stat-icon { color: var(--green); }
        .stat-card.amber .stat-icon { color: var(--amber); }
        .stat-card.blue .stat-icon { color: var(--blue); }
        .stat-card.red .stat-icon { color: var(--red); }

        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .badge-red {
          background: var(--red-bg);
          color: var(--red);
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
        }
        .badge-gold {
          background: var(--gold);
          color: var(--navy);
        }

        .btn-sm {
          padding: 4px 10px !important;
          font-size: 12px !important;
        }
        .btn-icon {
          padding: 4px 6px !important;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-gold {
          background: var(--gold) !important;
          color: var(--navy) !important;
          border: none !important;
        }
        .btn-gold:hover {
          background: var(--gold-dark) !important;
          color: var(--navy) !important;
        }

        .btn-ghost {
          background: transparent !important;
          color: var(--text) !important;
          border: 1px solid var(--border) !important;
        }
        .btn-ghost:hover {
          background: var(--cream) !important;
          color: var(--text) !important;
        }

        .btn-primary {
          background: var(--gold) !important;
          color: var(--navy) !important;
          border: none !important;
        }
        .btn-primary:hover {
          background: var(--gold-dark) !important;
          color: var(--navy) !important;
        }

        .empty-state {
          text-align: center;
          padding: 40px 0;
        }
        .empty-icon {
          font-size: 32px;
          margin-bottom: 12px;
          color: var(--text-light);
        }
        .empty-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text);
          margin-bottom: 4px;
        }
        .empty-text {
          font-size: 14px;
          color: var(--text-light);
        }

        .table-wrap {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          text-align: left;
          padding: 10px 12px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-light);
          border-bottom: 2px solid var(--border);
          background: var(--cream);
        }
        td {
          padding: 10px 12px;
          border-bottom: 1px solid var(--border-light);
          font-size: 13px;
        }
        tr:hover td {
          background: var(--cream);
        }

        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .table-wrap table {
            min-width: 700px;
          }
        }
        @media (max-width: 600px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .sender-ids-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
};

export default SmsBalance;